import { Chain } from '@1delta/chain-registry'
import type { SavingsRegistryEntry } from './registry'

/**
 * Venus **Liquidity Hub** — per-asset ERC-4626 allocator vaults on BNB Chain.
 *
 * A Hub takes one underlying and cascades it across three yield families
 * through a governance-ordered deposit queue:
 *
 *  | family | venue                       | holds        |
 *  | ------ | --------------------------- | ------------ |
 *  | Core   | Venus Core lending          | vTokens      |
 *  | Flux   | Fluid Lending (third party) | fTokens      |
 *  | FRV    | Venus Fixed-Rate Vaults     | 4626 shares  |
 *
 * Withdrawals walk a separate queue (`[Flux, Core, FRV]`) after the Hub's own
 * idle balance, and revert entirely rather than filling partially. Fees —
 * management, performance and a retained redeem fee — are all **0** on every
 * live Hub and read live by the reader, since each is an ACM-gated dial.
 *
 * ## Why these are not `instant`
 *
 * The FRV leg is a dated institutional loan, and `AdapterFRV.maxWithdraw`
 * returns **0 outside terminal states** — the coupon accrues into the share
 * price while the principal is undeliverable until the vault matures and
 * settles. That was 851 k of vhUSDT's 4.26 M and 200 k of vhU's 851 k when
 * these rows were written, on ~30-day terms. Hence `instant-capped` and the
 * `venus-hub` reader, which reproduces the Hub's `internal _aggregateLiquid()`
 * rather than reporting `totalAssets` as withdrawable.
 *
 * ## `solvency: 'overcollateralized'` — checked leg by leg, 2026-09-10
 *
 * Every registered leg is collateral-backed with an on-chain liquidation
 * threshold, so the label is asserted rather than left pending. The FRV leg is
 * the one that needed looking up, because its docs read like unsecured credit
 * ("principal and target interest are not guaranteed") — but that sentence
 * describes bad-debt risk, which is true of any lending market, not an absence
 * of collateral. `InstitutionalLoanVault` carries a `RiskConfig`
 * (`liquidationThreshold` / `liquidationIncentive` / `latePenaltyRate`), HF-
 * based liquidation through a `LiquidationAdapter`, and a deadline-based
 * liquidation for an overdue loan. Measured live on both open vaults:
 *
 *  | FRV                | collateral                        | value      | debt       | ratio | LT   |
 *  | ------------------ | --------------------------------- | ---------- | ---------- | ----- | ---- |
 *  | Solv BTCB (USDT)   | 21.47 vceBTC (Ceffu Custody BTC)  | $1,656,017 | $1,004,932 | 165 % | 90 % |
 *  | Asseto CASH+ (U)   | 7,000 CASH+ (CashPlus)            |   $767,186 |   $500,704 | 153 % | 85 % |
 *
 * Two qualifications that belong in the row's PROSE rather than the label,
 * because no enum value can carry them:
 *
 *  - **The collateral is custodial / RWA, not the pool kind.** `vceBTC` is a
 *    Ceffu-custody receipt and `CASH+` is Asseto's tokenised cash fund — both
 *    are overcollateralised on-chain and both depend on an off-chain
 *    custodian, and each FRV is ONE borrower against ONE collateral.
 *  - **"Its own lending markets" is not quite the shape.** Flux routes to
 *    **Fluid**, a third-party money market (`fUSDT` / `fUSDC` / `fU`), and an
 *    FRV is a bilateral institutional loan rather than a Venus Core market.
 *    All three are overcollateralised; only one of them is Venus's own pool.
 *
 * The registered set is governance-mutable (`addYieldGroup` is ACM-gated), so
 * this is a statement about the CURRENT roster — which is exactly what the
 * pinned-roster assertion in `venusHubRows.test.ts` keeps honest: a group
 * Venus adds that we have not looked at fails the test before it can quietly
 * sit behind this label.
 *
 * ## Deployment
 *
 * BNB Chain only. `HubRegistry` (chain-level singleton)
 * `0x6d93fd479f2d37445cfbe132412e316a0364acc2` enumerates the roster via
 * `getHubs()`; the testnet deployment is out of scope. Contracts verified on
 * Sourcify (`exact_match`) — but note the Hub, YieldGroups and adapters are
 * **absent from Venus's published audit index**, on a stack first deployed
 * 2026-07-29.
 */

/** Venus Core pool Comptroller (Unitroller/Diamond) on BNB Chain. All three
 *  Hubs' Core markets live in it, so no isolated-pool `RewardsDistributor`
 *  enumeration is needed — `venusSupplySpeeds` is the whole supply-side
 *  emission surface. */
const VENUS_CORE_COMPTROLLER = '0xfd36e2c2a6789db23113685031d7f16329158384'

/** Venus `ResilientOracle`. Pinned rather than read from
 *  `comptroller.oracle()` because a discovered address cannot be a call target
 *  in the same multicall round; the live test asserts the two agree. */
const VENUS_RESILIENT_ORACLE = '0x6592b5de802159f3e74b2486b091d11a8256ab8a'

/** XVS — the Core pool's emission token. */
const XVS = '0xcf6bb5389c92bdda8a3747ddb454cb7a64626c63'

/**
 * Shared across all three rows: 24-decimal shares over an 18-decimal asset
 * (`decimalsOffset = 6`), non-rebasing, permissionless mint, capped instant
 * exit. `depositCapacity` comes from the Hub's own `maxDeposit`, which is a
 * real aggregate cap rather than `uint256.max`, so the reader publishes it.
 */
const hubBase = {
  reader: 'venus-hub' as const,
  brand: 'Venus',
  // Share decimals are 24 — `decimalsOffset(6)` on an 18-decimal underlying.
  // Getting this wrong is silent: the exchange rate reads 1e-6 of par.
  decimals: 24,
  underlyingDecimals: 18,
  isRebasing: false,
  isMintable: true,
  withdrawalMode: 'instant-capped' as const,
  // Asserted, not assumed — every leg is collateral-backed with an on-chain
  // liquidation threshold, incl. the FRV one whose docs read like unsecured
  // credit. See the note above for the live ratios and the two qualifications
  // that live in the descriptions instead.
  solvency: 'overcollateralized' as const,
  // No `yieldFetcher`: the rate is the groups' TVL-weighted `spotAPYBps()`,
  // which the reader computes from the same block's `totalAssets()` reads.
  // There is nothing off-chain to fall back to — the Hub publishes no APY
  // view by design, `api.venus.io` has no Hub route, and DefiLlama carries
  // only `venus-core-pool` / `venus-flux` pools.
}

/** The exit-liquidity clause every row repeats, so the three descriptions
 *  stay identical where the mechanics are identical. */
const EXIT_PROSE =
  'Exits are same-transaction and free, but capped at the liquid legs: anything sitting in a Fixed-Rate Vault is locked until it matures, typically a 30-day term.'

/** The backing clause. Every leg is overcollateralised — which the row's
 *  `solvency` states — but two things the label cannot carry belong next to
 *  it: one of the three venues is not Venus's, and the fixed-term leg's
 *  collateral is a custody receipt or an RWA token against a single borrower. */
const BACKING_PROSE =
  'Every leg is overcollateralised, though not all of it is Venus: Flux lends through third-party Fluid, and a Fixed-Rate Vault is one institutional borrower posting custodial or real-world-asset collateral rather than a pool.'

export const VENUS_HUB_ENTRIES: {
  [chainId: string]: SavingsRegistryEntry[]
} = {
  [Chain.BNB_SMART_CHAIN_MAINNET]: [
    {
      ...hubBase,
      address: '0x18afdacf30f8671021dec4b78297e39d2fe87226',
      underlying: '0x55d398326f99059ff775485246999027b3197955',
      symbol: 'vhUSDT',
      description: `Venus's USDT Liquidity Hub cascades one deposit across Venus Core lending, Fluid and Venus Fixed-Rate Vaults; the share price is the blend. ${EXIT_PROSE} ${BACKING_PROSE}`,
      venusHub: {
        yieldGroups: [
          '0xc9e6ced9589363f8dc5695be2c79ab4ddaecc94b',
          '0xe3df38e12e37ed80e1b3ccf2bdf84f9e1527ce14',
          '0x621ef38ce0c4e7060ff0bf3d609e3d46ec144be7',
        ],
        core: {
          yieldGroup: '0xc9e6ced9589363f8dc5695be2c79ab4ddaecc94b',
          vToken: '0xfd5840cd36d94d7229439859c0112a4185bc0255',
          comptroller: VENUS_CORE_COMPTROLLER,
          oracle: VENUS_RESILIENT_ORACLE,
          rewardToken: XVS,
        },
      },
    },
    {
      ...hubBase,
      address: '0x9d2d9592cf8dfbf59107faab703d08494be14617',
      underlying: '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d',
      symbol: 'vhUSDC',
      description: `Venus's USDC Liquidity Hub cascades one deposit across Venus Core lending, Fluid and Venus Fixed-Rate Vaults; the share price is the blend. ${EXIT_PROSE} ${BACKING_PROSE}`,
      venusHub: {
        yieldGroups: [
          '0x299d9be7cefff91c68f13f267d525cfc18e965ef',
          '0xa65bb4b20542268b64cf08871a98d75342afe927',
          '0x438388847ee16850ab4f5b82dc7954c0d043b716',
        ],
        core: {
          yieldGroup: '0x299d9be7cefff91c68f13f267d525cfc18e965ef',
          vToken: '0xeca88125a5adbe82614ffc12d0db554e2e2867c8',
          comptroller: VENUS_CORE_COMPTROLLER,
          oracle: VENUS_RESILIENT_ORACLE,
          rewardToken: XVS,
        },
      },
    },
    {
      ...hubBase,
      address: '0x0e5aa174d4f31b757a237eb1999de151596788b0',
      underlying: '0xce24439f2d9c6a2289f741120fe202248b666666',
      symbol: 'vhU',
      description: `Venus's Liquidity Hub for U (United Stables) cascades one deposit across Venus Core lending, Fluid and Venus Fixed-Rate Vaults; the share price is the blend. ${EXIT_PROSE} ${BACKING_PROSE}`,
      venusHub: {
        yieldGroups: [
          '0x8a680f77a5367fa7cd33a02f51896cb1d55159c3',
          '0xe31b8851c3fa9b3dd39a04a2ed9493869a410616',
          '0x30908eddb9e94add7ac9944a0adda66d80b89143',
        ],
        core: {
          yieldGroup: '0x8a680f77a5367fa7cd33a02f51896cb1d55159c3',
          vToken: '0x3d5e269787d562b74acc55f18bd26c5d09fa245e',
          comptroller: VENUS_CORE_COMPTROLLER,
          oracle: VENUS_RESILIENT_ORACLE,
          rewardToken: XVS,
        },
      },
    },
  ],
}

/** The chain-level `HubRegistry`, exported for the live roster test. */
export const VENUS_HUB_REGISTRY: { [chainId: string]: string } = {
  [Chain.BNB_SMART_CHAIN_MAINNET]: '0x6d93fd479f2d37445cfbe132412e316a0364acc2',
}
